/**
 * Wo ein stillgelegtes Gerät gehangen hat (#780).
 *
 * Beim Stilllegen räumt der Server `floor`/`space`/`x`/`y` vom Asset und legt die Ids in
 * `archivedLocation` ab. Für den Nachweis ist die eigentliche Frage aber nicht die Etage,
 * sondern das Objekt: Liegenschaft und Gebäude. Beides steht nicht am Gerät, sondern hängt an
 * der Etage (`Floor.building`) bzw. an der Liegenschaft, die das Gebäude führt
 * (`Site.buildings`) — dieser Modul löst die Kette in einem Rutsch für eine ganze Liste auf.
 *
 * Jeder Schritt ist einzeln abgesichert: fällt einer aus, fehlt nur sein Namensteil, die Liste
 * bleibt benutzbar und zeigt an dieser Stelle die Id.
 */

import { Building, Floor, Site, Space, from, is } from "@the-real-insight/tri-model";

import { userAPI } from "./../UserAPI";
import { entityIdAsString } from "./buildingLookup";

export interface ArchivedLocationParts {
  site?: string;
  building?: string;
  floor?: string;
  space?: string;
}

interface WithArchivedLocation {
  _id?: any;
  archivedLocation?: { floor?: string; space?: string };
}

const uniqueIds = (values: Array<string | undefined>): string[] =>
  Array.from(new Set(values.filter((value): value is string => !!value)));

const queryByIds = async (type: any, ids: string[]): Promise<any[]> => {
  if (ids.length === 0) return [];

  const rows = await userAPI.query(from(type).where(is("_id").in(ids) as any));

  return Array.isArray(rows) ? rows : [];
};

const byId = (rows: any[]): Record<string, any> =>
  rows.reduce((map: Record<string, any>, row: any) => {
    const id = entityIdAsString(row?._id);
    if (id) map[id] = row;

    return map;
  }, {});

/**
 * Namen für die archivierten Standorte aller übergebenen Geräte, je Gerät unter dessen `_id`.
 * Ein Teil fehlt, wenn er nicht auflösbar ist — die Anzeige fällt dann auf die Id zurück.
 */
export async function resolveArchivedLocations(
  assets: WithArchivedLocation[],
): Promise<Record<string, ArchivedLocationParts>> {
  const floorIds = uniqueIds(assets.map((asset) => asset.archivedLocation?.floor));
  const spaceIds = uniqueIds(assets.map((asset) => asset.archivedLocation?.space));

  const [floors, spaces] = await Promise.all([
    queryByIds(Floor.type, floorIds).catch(() => []),
    queryByIds(Space.type, spaceIds).catch(() => []),
  ]);

  const floorsById = byId(floors);
  const spacesById = byId(spaces);

  const buildingIds = uniqueIds(floors.map((floor: any) => entityIdAsString(floor?.building)));
  const buildings = await queryByIds(Building.type, buildingIds).catch(() => []);
  const buildingsById = byId(buildings);

  // Die Liegenschaft führt ihre Gebäude als Liste — in MongoDB trifft `$in` auf einem Array
  // jede Liegenschaft, die eines der gesuchten Gebäude nennt.
  const sites = buildingIds.length
    ? await userAPI
        .query(from(Site.type).where(is("buildings").in(buildingIds) as any))
        .then((rows: any) => (Array.isArray(rows) ? rows : []))
        .catch(() => [])
    : [];

  const siteNameByBuilding: Record<string, string> = {};
  for (const site of sites) {
    for (const entry of Array.isArray(site?.buildings) ? site.buildings : []) {
      const buildingId = entityIdAsString(entry);
      if (buildingId && site?.name) siteNameByBuilding[buildingId] = site.name;
    }
  }

  return assets.reduce((result: Record<string, ArchivedLocationParts>, asset) => {
    const assetId = entityIdAsString(asset?._id);
    if (!assetId) return result;

    const floorId = asset.archivedLocation?.floor;
    const spaceId = asset.archivedLocation?.space;
    const floor = floorId ? floorsById[floorId] : undefined;
    const buildingId = entityIdAsString(floor?.building);

    result[assetId] = {
      site: buildingId ? siteNameByBuilding[buildingId] : undefined,
      building: buildingId ? buildingsById[buildingId]?.name : undefined,
      floor: floor?.name,
      space: spaceId ? spacesById[spaceId]?.name : undefined,
    };

    return result;
  }, {});
}

/** Anzeigetext der Kette; nicht auflösbare Teile fallen auf ihre Id zurück. */
export function describeArchivedLocation(
  asset: WithArchivedLocation,
  parts: ArchivedLocationParts | undefined,
): string {
  const location = asset.archivedLocation;
  if (!location) return "—";

  const chain = [
    parts?.site,
    parts?.building,
    location.floor ? (parts?.floor ?? location.floor) : undefined,
    location.space ? (parts?.space ?? location.space) : undefined,
  ].filter(Boolean);

  return chain.length > 0 ? chain.join(" · ") : "—";
}
